/**
 * Map view for a travel trip. Plots one pin per day (placed at the day's
 * geocoded coordinates) and one pin per event that has coordinates. Days are
 * connected by a solid polyline in day-order; transit segments are drawn as a
 * dashed line between the surrounding days.
 *
 * The view is a feature — when the `map` flag is off the caller must not
 * render it, so this file never has to check the flag itself.
 *
 * Leaflet notes:
 *   - Vite's asset pipeline breaks Leaflet's built-in default icon URL lookup,
 *     so we replace the default icon with explicit CDN URLs once at module load.
 *   - `leaflet/dist/leaflet.css` is a side-effect import — bundlers include it
 *     only in client builds. The SSR guard around the icon patch is defensive
 *     so any future prerender step wouldn't blow up.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ChevronDown, Loader2, MapPin, RefreshCw } from 'lucide-react';
import type { TravelEventResponse, TravelItemResponse, TravelTripResponse } from '../api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/use-toast';
import { useTravelMapGenerator } from '../hooks/useTravelMapGenerator';
import { getDayColor, formatDayDate } from './TravelHelpers';

// Replace Leaflet's default icon URLs (Vite-safe CDN fallbacks). This is a
// one-time side effect and is skipped on the server where `L` / the DOM aren't
// meaningfully available.
if (typeof window !== 'undefined') {
  // @ts-expect-error Internal Leaflet symbol — documented workaround for Vite.
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });
}

interface TravelMapViewProps {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  activeItemId: string | null;
  onSelectItem: (itemId: string) => void;
}

interface DayMarker {
  /** All day items rendered at this exact location, sorted by day number. */
  days: TravelItemResponse[];
  lat: number;
  lng: number;
  color: string;
  /** Compact label, e.g. "3" or "2-4,7". */
  label: string;
}

/**
 * Collapses a sorted list of day numbers into a compact human label.
 * `[2, 3, 4, 7]` → `"2-4,7"`, `[5]` → `"5"`, `[1, 3]` → `"1,3"`.
 * Runs of 2 (e.g. `[2, 3]`) stay as "2,3"; only 3+ consecutive collapse to a range,
 * because "2-3" reads as roughly the same length as "2,3" but loses information.
 */
export function formatDayRanges(daysSortedAsc: number[]): string {
  if (daysSortedAsc.length === 0) return '';
  const parts: string[] = [];
  let runStart = daysSortedAsc[0];
  let runEnd = daysSortedAsc[0];
  const flush = () => {
    if (runEnd - runStart >= 2) parts.push(`${runStart}-${runEnd}`);
    else for (let n = runStart; n <= runEnd; n++) parts.push(String(n));
  };
  for (let i = 1; i < daysSortedAsc.length; i++) {
    const n = daysSortedAsc[i];
    if (n === runEnd + 1) {
      runEnd = n;
    } else {
      flush();
      runStart = n;
      runEnd = n;
    }
  }
  flush();
  return parts.join(',');
}

/**
 * Quantize a coordinate so floating-point noise doesn't split otherwise-equal
 * geocodes into separate marker groups. 6 decimals ≈ 11cm at the equator —
 * tighter than any geocoder is honest about, looser than IEEE-754 jitter.
 */
export function quantizeCoord(value: number): string {
  return value.toFixed(6);
}

interface EventMarker {
  event: TravelEventResponse;
  item: TravelItemResponse;
  lat: number;
  lng: number;
  color: string;
}

/**
 * Pill-shaped DivIcon so the marker stays circular for single-digit labels
 * but expands horizontally to fit multi-day ranges (e.g. "2-4,7"). Using a
 * DivIcon avoids shipping a sprite per color and stays crisp at any zoom.
 *
 * iconSize is an approximation — DivIcon doesn't need pixel-perfect bounds
 * because the actual element auto-sizes — but we estimate it so Leaflet can
 * anchor the marker reasonably without measuring the DOM.
 */
function buildDayIcon(color: string, label: string, highlighted: boolean): L.DivIcon {
  const height = highlighted ? 30 : 24;
  const border = highlighted ? '3px solid #ffffff' : '2px solid #ffffff';
  const shadow = highlighted
    ? '0 0 0 2px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.35)'
    : '0 1px 4px rgba(0,0,0,0.4)';
  // ~7px per character is close enough for our 11px font; add 2*8px padding.
  const estimatedWidth = Math.max(height, label.length * 7 + 16);
  return L.divIcon({
    className: 'travel-map-day-marker',
    html:
      `<div style="height:${height}px;min-width:${height}px;border-radius:9999px;` +
      `padding:0 8px;background:${color};border:${border};box-shadow:${shadow};` +
      `color:#fff;font-size:11px;font-weight:700;line-height:1;` +
      `display:inline-flex;align-items:center;justify-content:center;white-space:nowrap;">${label}</div>`,
    iconSize: [estimatedWidth, height],
    iconAnchor: [estimatedWidth / 2, height / 2],
  });
}

function buildEventIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: 'travel-map-event-marker',
    html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
}

/**
 * Popup bodies are rendered by React through a portal into a container element
 * that Leaflet owns, rather than being interpolated into an HTML string. That
 * is deliberate: popups display user-authored trip content (day titles, city
 * names, event titles), and building markup by string concatenation would make
 * this an XSS sink. Going through React keeps its escaping.
 */
interface PopupHost {
  key: string;
  el: HTMLElement;
}

function DayPopupBody({ marker, tripStartDate }: { marker: DayMarker; tripStartDate: string }) {
  const isGroup = marker.days.length > 1;
  const primary = marker.days[0];
  // City/country usually agree across grouped days (same geocode), but fall
  // back to whichever day actually has the text.
  const cityCountry = marker.days
    .map((d) => (d.city ? `${d.city}${d.country ? `, ${d.country}` : ''}` : ''))
    .find(Boolean);
  return (
    <div className="text-sm">
      {isGroup ? (
        <>
          <div className="font-semibold">Days {marker.label}</div>
          {cityCountry ? <div className="text-xs mt-0.5">{cityCountry}</div> : null}
          <ul className="mt-1 space-y-0.5 text-xs">
            {marker.days.map((d) => (
              <li key={d.itemId}>
                <span className="font-medium">Day {d.dayNumber}</span>
                {d.dayTitle ? ` — ${d.dayTitle}` : ''}
                <span className="opacity-60">
                  {' '}
                  · {formatDayDate(tripStartDate, d.dayNumber ?? 1)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <div className="font-semibold">
            Day {primary.dayNumber}
            {primary.dayTitle ? ` — ${primary.dayTitle}` : ''}
          </div>
          <div className="text-xs opacity-70">
            {formatDayDate(tripStartDate, primary.dayNumber ?? 1)}
          </div>
          {cityCountry ? <div className="text-xs mt-1">{cityCountry}</div> : null}
        </>
      )}
    </div>
  );
}

function EventPopupBody({ marker }: { marker: EventMarker }) {
  return (
    <div className="text-sm">
      <div className="font-semibold">{marker.event.title}</div>
      {marker.event.time ? <div className="text-xs opacity-70">{marker.event.time}</div> : null}
      {marker.event.hasAlternateLocations ? (
        <div className="text-[11px] mt-1.5 italic opacity-80">
          Other locations possible — this pin shows the branch closest to your day&apos;s
          accommodation.
        </div>
      ) : null}
    </div>
  );
}

export function TravelMapView({
  trip,
  items,
  events,
  activeItemId,
  onSelectItem,
}: TravelMapViewProps) {
  const { generatePins, isGenerating } = useTravelMapGenerator(trip.tripId);

  // How many rows are still missing coordinates? Used to enable/disable the
  // "Generate pins" button and to phrase its tooltip.
  const missingCoordCounts = useMemo(() => {
    let days = 0;
    let eventsMissing = 0;
    for (const it of items) {
      if (it.itemType === 'day' && (it.lat == null || it.lng == null)) days += 1;
    }
    for (const e of events) {
      if (e.lat == null || e.lng == null) eventsMissing += 1;
    }
    return { days, events: eventsMissing, total: days + eventsMissing };
  }, [items, events]);

  const runGeneratePins = async (options: { force?: boolean } = {}) => {
    const result = await generatePins(items, events, options);
    if (result.attempted === 0 && result.skipped === 0) {
      toast({ title: 'All pins already placed', description: 'Nothing to generate.' });
      return;
    }
    const parts: string[] = [];
    if (result.succeeded > 0) parts.push(`${result.succeeded} placed`);
    if (result.failed > 0) parts.push(`${result.failed} not found`);
    if (result.skipped > 0) parts.push(`${result.skipped} skipped (no city/address)`);
    toast({
      title: result.succeeded > 0 ? 'Map updated' : 'Generate pins finished',
      description: parts.join(' · ') || 'No changes.',
    });
  };

  /**
   * Group days that geocoded to the same spot into a single marker so a
   * 4-night Paris stay shows as one "2-5" pin instead of stacking four pins
   * where only the highest day number is visible. Color comes from the
   * lowest day in the group so the natural Day-1-first palette ordering
   * isn't visually disrupted.
   */
  const dayMarkers = useMemo<DayMarker[]>(() => {
    const groups = new Map<string, TravelItemResponse[]>();
    for (const i of items) {
      if (i.itemType !== 'day' || i.lat == null || i.lng == null) continue;
      const key = `${quantizeCoord(i.lat)},${quantizeCoord(i.lng)}`;
      const list = groups.get(key) ?? [];
      list.push(i);
      groups.set(key, list);
    }
    const result: DayMarker[] = [];
    for (const [, list] of groups) {
      const sorted = list
        .slice()
        .sort((a, b) => (a.dayNumber ?? Infinity) - (b.dayNumber ?? Infinity));
      const dayNumbers = sorted
        .map((i) => i.dayNumber)
        .filter((n): n is number => typeof n === 'number');
      const label = dayNumbers.length > 0 ? formatDayRanges(dayNumbers) : '';
      result.push({
        days: sorted,
        lat: sorted[0].lat as number,
        lng: sorted[0].lng as number,
        color: getDayColor(sorted[0].dayNumber ?? 1),
        label,
      });
    }
    return result;
  }, [items]);

  const eventMarkers = useMemo<EventMarker[]>(() => {
    const itemsById = new Map(items.map((i) => [i.itemId, i]));
    return events
      .filter((e) => e.lat != null && e.lng != null)
      .map((e) => {
        const item = itemsById.get(e.itemId);
        if (!item) return null;
        return {
          event: e,
          item,
          lat: e.lat as number,
          lng: e.lng as number,
          color: getDayColor(item.dayNumber ?? 1),
        };
      })
      .filter((m): m is EventMarker => m !== null);
  }, [items, events]);

  // Solid polylines: connect each day's events in their recorded order, so the
  // user can see the within-day path.
  const eventPolylines = useMemo(() => {
    const byItem = new Map<string, EventMarker[]>();
    for (const em of eventMarkers) {
      const list = byItem.get(em.item.itemId) ?? [];
      list.push(em);
      byItem.set(em.item.itemId, list);
    }
    const lines: Array<{ key: string; color: string; points: Array<[number, number]> }> = [];
    for (const [itemId, markers] of byItem) {
      if (markers.length < 2) continue;
      const sorted = markers.slice().sort((a, b) => a.event.sortOrder - b.event.sortOrder);
      lines.push({
        key: `day-${itemId}`,
        color: sorted[0].color,
        points: sorted.map((m) => [m.lat, m.lng]),
      });
    }
    return lines;
  }, [eventMarkers]);

  // Dashed transit polylines: for each transit item, draw from the previous
  // day's pin to the next day's pin (skipping over the transit row itself).
  const transitPolylines = useMemo(() => {
    const lines: Array<{ key: string; points: Array<[number, number]>; color: string }> = [];
    // Every day item that landed in a DayMarker (including grouped ones) maps
    // back to its marker, so transit endpoints work whether or not the day
    // was deduplicated.
    const dayByItemId = new Map<string, DayMarker>();
    for (const m of dayMarkers) {
      for (const d of m.days) dayByItemId.set(d.itemId, m);
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.itemType !== 'transit') continue;
      // Find previous day with coords
      let prevDay: DayMarker | undefined;
      for (let j = i - 1; j >= 0; j--) {
        const found = dayByItemId.get(items[j].itemId);
        if (found) {
          prevDay = found;
          break;
        }
      }
      // Find next day with coords
      let nextDay: DayMarker | undefined;
      for (let j = i + 1; j < items.length; j++) {
        const found = dayByItemId.get(items[j].itemId);
        if (found) {
          nextDay = found;
          break;
        }
      }
      if (prevDay && nextDay) {
        lines.push({
          key: `transit-${item.itemId}`,
          points: [
            [prevDay.lat, prevDay.lng],
            [nextDay.lat, nextDay.lng],
          ],
          color: '#6b7280',
        });
      }
    }

    return lines;
  }, [items, dayMarkers]);

  const allPoints = useMemo<Array<[number, number]>>(() => {
    const pts: Array<[number, number]> = [];
    for (const d of dayMarkers) pts.push([d.lat, d.lng]);
    for (const e of eventMarkers) pts.push([e.lat, e.lng]);
    return pts;
  }, [dayMarkers, eventMarkers]);

  // --- Leaflet lifecycle -------------------------------------------------
  // Driven directly rather than through a React wrapper. The wrapper was
  // dropped because it is not distributed under an OSI-approved licence, and
  // this component only ever used six of its primitives — all of which have
  // one-line imperative equivalents.

  const mapRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.LayerGroup | null>(null);
  const [popupHosts, setPopupHosts] = useState<PopupHost[]>([]);

  // Day markers by item id, so a highlight change can call setIcon on the one
  // marker that changed instead of rebuilding the layer group.
  const dayMarkerRegistry = useRef(new Map<string, { marker: L.Marker; dm: DayMarker }>());

  // Keep the selection callback out of the layer effect's dependencies: a
  // parent that recreates it each render would otherwise tear down and rebuild
  // every marker on every render, closing any open popup as it went.
  const onSelectItemRef = useRef(onSelectItem);
  onSelectItemRef.current = onSelectItem;

  // Read inside the layer effect so a marker is built with the correct initial
  // highlight, without making the highlight a reason to rebuild the layers.
  const activeItemIdRef = useRef(activeItemId);
  activeItemIdRef.current = activeItemId;

  const initialCenter = useMemo<[number, number] | null>(() => {
    if (dayMarkers[0] != null) return [dayMarkers[0].lat, dayMarkers[0].lng];
    if (eventMarkers[0] != null) return [eventMarkers[0].lat, eventMarkers[0].lng];
    return null;
  }, [dayMarkers, eventMarkers]);

  // Attach via a callback ref rather than a mount effect. The map element is
  // absent while the trip has no coordinates (see the empty state below), so a
  // `[]` effect would run once, find no node, and never run again — leaving an
  // empty container after "Generate pins" produced coordinates. A callback ref
  // fires whenever the node attaches or detaches, which is exactly the signal.
  //
  // `initialCenter` is read, not depended on: re-centring is the fit-bounds
  // effect's job, and rebuilding the map on data change would discard the
  // user's pan/zoom.
  const initialCenterRef = useRef(initialCenter);
  initialCenterRef.current = initialCenter;
  const attachMap = useCallback((el: HTMLDivElement | null) => {
    if (el == null) {
      mapRef.current?.remove();
      mapRef.current = null;
      layersRef.current = null;
      dayMarkerRegistry.current.clear();
      return;
    }
    if (mapRef.current != null) return;
    const map = L.map(el, {
      center: initialCenterRef.current ?? [0, 0],
      zoom: 6,
      scrollWheelZoom: true,
      // Tame trackpad zoom. Leaflet's defaults (wheelPxPerZoomLevel: 60,
      // zoomSnap: 1) jump a full zoom level for every ~60px of scroll, so a
      // single trackpad pinch-out fires several events and rockets through
      // 3–4 levels. Doubling the px threshold + half-step snap matches how
      // Mapbox/Google Maps feel on a trackpad.
      wheelPxPerZoomLevel: 120,
      wheelDebounceTime: 100,
      zoomSnap: 0.5,
      zoomDelta: 0.5,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    layersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
  }, []);

  // Rebuild markers and polylines whenever the derived geometry changes.
  const dayMarkersKey = dayMarkers.map((d) => `${d.lat},${d.lng},${d.label},${d.color}`).join('|');
  const eventMarkersKey = eventMarkers
    .map((e) => `${e.event.eventId},${e.lat},${e.lng},${e.color}`)
    .join('|');
  const polylinesKey = [...transitPolylines, ...eventPolylines]
    .map((l) => `${l.key}:${l.color}:${l.points.map((p) => p.join(',')).join(';')}`)
    .join('|');

  useEffect(() => {
    const layers = layersRef.current;
    if (layers == null) return;
    layers.clearLayers();
    const hosts: PopupHost[] = [];

    for (const line of transitPolylines) {
      L.polyline(line.points, {
        color: line.color,
        weight: 2,
        dashArray: '6 6',
        opacity: 0.7,
      }).addTo(layers);
    }
    for (const line of eventPolylines) {
      L.polyline(line.points, { color: line.color, weight: 3, opacity: 0.6 }).addTo(layers);
    }

    dayMarkerRegistry.current.clear();
    for (const dm of dayMarkers) {
      const primary = dm.days[0];
      const highlighted = dm.days.some((d) => d.itemId === activeItemIdRef.current);
      const host = document.createElement('div');
      hosts.push({ key: `day-${primary.itemId}`, el: host });
      const marker = L.marker([dm.lat, dm.lng], {
        icon: buildDayIcon(dm.color, dm.label || String(primary.dayNumber ?? ''), highlighted),
      })
        .on('click', () => onSelectItemRef.current(primary.itemId))
        .bindPopup(host)
        .addTo(layers);
      dayMarkerRegistry.current.set(primary.itemId, { marker, dm });
    }

    for (const em of eventMarkers) {
      const host = document.createElement('div');
      hosts.push({ key: `event-${em.event.eventId}`, el: host });
      L.marker([em.lat, em.lng], { icon: buildEventIcon(em.color) })
        .on('click', () => onSelectItemRef.current(em.item.itemId))
        .bindPopup(host)
        .addTo(layers);
    }

    setPopupHosts(hosts);
    // Keyed on serialized geometry rather than array identity so a parent
    // re-render that produces equivalent data does not rebuild every layer.
    //
    // activeItemId is deliberately NOT a dependency. Clicking a marker opens
    // its popup and sets activeItemId; if that rebuilt the layer group, the
    // clearLayers() below would destroy the marker Leaflet had just opened a
    // popup on, and the popup would vanish on click. Highlighting is applied
    // by the setIcon effect underneath instead.
    // No `mapReady` dependency. React attaches refs during commit, before any
    // effect runs, so `layersRef` is already populated the first time this
    // fires. And the map element only exists when there is geometry to draw,
    // so the keys below change on exactly the transition that creates it.
    // Depending on a readiness flag as well made this run twice per mount,
    // rebuilding every marker immediately after creating it.
  }, [dayMarkersKey, eventMarkersKey, polylinesKey]);

  // Highlighting only: repaint the icons of the markers whose selected-state
  // changed, leaving the markers themselves — and any open popup — in place.
  useEffect(() => {
    for (const { marker, dm } of dayMarkerRegistry.current.values()) {
      const highlighted = dm.days.some((d) => d.itemId === activeItemId);
      marker.setIcon(
        buildDayIcon(dm.color, dm.label || String(dm.days[0].dayNumber ?? ''), highlighted),
      );
    }
  }, [activeItemId, popupHosts]);

  // Fit to whatever markers are present, re-centring only when the point set
  // actually changes. This deliberately does not run during render: doing so
  // fired on every parent re-render — including ones triggered by the user
  // zooming — so each trackpad pinch was instantly snapped back.
  const pointsSignature = allPoints.map(([lat, lng]) => `${lat},${lng}`).join('|');
  useEffect(() => {
    const map = mapRef.current;
    if (map == null || allPoints.length === 0) return;
    if (allPoints.length === 1) {
      map.setView(allPoints[0], 11);
    } else {
      map.fitBounds(allPoints, { padding: [40, 40] });
    }
    // pointsSignature is the deduplicated change-trigger — re-running on the
    // array identity itself would defeat the purpose. (The project's ESLint
    // config doesn't register react-hooks/exhaustive-deps and uses
    // --report-unused-disable-directives, so a disable directive would itself
    // be a lint error — this comment is the only valid expression of intent.)
  }, [pointsSignature]);

  const dayMarkerByKey = useMemo(() => {
    const m = new Map<string, DayMarker>();
    for (const dm of dayMarkers) m.set(`day-${dm.days[0].itemId}`, dm);
    return m;
  }, [dayMarkers]);
  const eventMarkerByKey = useMemo(() => {
    const m = new Map<string, EventMarker>();
    for (const em of eventMarkers) m.set(`event-${em.event.eventId}`, em);
    return m;
  }, [eventMarkers]);

  if (dayMarkers.length === 0 && eventMarkers.length === 0) {
    const hasAnySourceText = items.some(
      (i) => i.itemType === 'day' && (i.city || i.accommodationAddress),
    );
    return (
      <div className="flex items-center justify-center h-full text-center p-8">
        <div className="max-w-sm space-y-3">
          <p className="text-sm font-medium">No coordinates yet</p>
          <p className="text-xs text-muted-foreground">
            Add a city and country to a day (or an address to an event) and the map will place a pin
            automatically. You can also re-trigger the lookup from the day editor.
          </p>
          {hasAnySourceText && (
            <Button size="sm" onClick={() => void runGeneratePins()} disabled={isGenerating}>
              {isGenerating ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <MapPin className="h-3 w-3 mr-1" /> Generate pins from entries
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full relative">
      {/* Floating "Generate pins" control. Leaflet panes are z-index 200–700;
          z-50 in tailwind is 50, so bump the wrapper with an inline style. */}
      <div className="absolute top-3 right-3 flex" style={{ zIndex: 1000 }}>
        <Button
          size="sm"
          variant="secondary"
          className="shadow-md rounded-r-none"
          onClick={() => void runGeneratePins()}
          disabled={isGenerating || missingCoordCounts.total === 0}
          title={
            missingCoordCounts.total === 0
              ? 'All days and events already have pins — use Regenerate to overwrite'
              : `Geocode ${missingCoordCounts.total} missing ${
                  missingCoordCounts.total === 1 ? 'pin' : 'pins'
                } from day/event text`
          }
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating…
            </>
          ) : (
            <>
              <MapPin className="h-3 w-3 mr-1" />
              {missingCoordCounts.total === 0
                ? 'All pins placed'
                : `Generate ${missingCoordCounts.total} pin${missingCoordCounts.total === 1 ? '' : 's'}`}
            </>
          )}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="secondary"
              className="shadow-md rounded-l-none border-l border-border px-2"
              disabled={isGenerating}
              title="More options"
            >
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => void runGeneratePins({ force: true })}>
              <RefreshCw className="h-4 w-4 mr-2" /> Regenerate all pins
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div ref={attachMap} style={{ height: '100%', width: '100%' }} />
      {popupHosts.map((host) => {
        const dm = dayMarkerByKey.get(host.key);
        if (dm != null) {
          return createPortal(
            <DayPopupBody marker={dm} tripStartDate={trip.startDate} />,
            host.el,
            host.key,
          );
        }
        const em = eventMarkerByKey.get(host.key);
        if (em != null) {
          return createPortal(<EventPopupBody marker={em} />, host.el, host.key);
        }
        return null;
      })}
    </div>
  );
}
